import {Dce, DceComponent, DceHTMLElement, DceEngineEventDetails, DceEngine, DceEngineEvents} from "dce-engine";
import {DceAjax, DceAjaxResponse} from "dce-ajax";
import {AjaxResp, Events, FragmentAction, KnownActionData} from "./ajax/AjaxResponse";
import {DomCommands} from "./ajax/DomManipulation";
import wireActionParse, {ParsedWireAction} from "./utils/wireActionParse";
import serializeFormInputs from "./utils/serializeFormInputs";
import FragmentWireEventHandler from "./dceplugin/FragmentWireEventHandler";
import {ModalFragment, FragmentsContext} from "../index";

export interface FragmentProps {
    //klasa po stronie serwera
    name: string,
    //propertiesy z serwera
    props: string,
    //tagi do określenia roli fragmentu
    roles: string,
}

@Dce("Fragment")
export default class Fragment extends DceComponent<FragmentProps> {

    private parent: Fragment;

    private wireEvents: FragmentWireEventHandler;

    private calls: Map<string, (a: FragmentAction<keyof KnownActionData>[]) => void>
        = new Map<string, (a: FragmentAction<keyof KnownActionData>[]) => void>();

    private loadingElement: Array<[HTMLElement, string]> = [];
    private loadingCounter = 0;

    public wireEventsLocks: Map<string, boolean> = new Map<string, boolean>();

    public formDataSerializer: (elem: HTMLElement) => FormData;

    constructor(elem: DceHTMLElement, props: FragmentProps) {
        super(elem, props);

        Events.addEventListeners(
            this.elem,
            async (e) => {
                this.onOpenModalEvent(e);
            },
            (e) => {
                this.onDom(e);
            },
            (e) => {
                this.onReplaceFragmentViewEvent(e);
            },
            (e) => {
                this.onReplaceFragmentEvent(e);
            },
            async (e) => {
                this.onServerEvent(e);
            },
            e => {
                this.onUiEvent(e);
            },
            e => {
                this.props.props = e.detail.p;
            }
        );

        this.findLoadingElement();

        this.wireEvents = new FragmentWireEventHandler(this.elem, this);
        this.registerCalls();

    }

    public setFormDataSerializer(serializer: (elem: HTMLElement) => FormData) {
        this.formDataSerializer = serializer;
    }

    public serializeForm(elem: HTMLElement): FormData {
        if (null == this.formDataSerializer) {
            return new FormData(elem as HTMLFormElement);
        }
        return this.formDataSerializer(elem);
    }

    private hasTag(tag: string): boolean {
        if (null == tag) {
            return true;
        }
        return this.props.roles.indexOf("/" + tag + "/") > -1;
    }

    public registerCalls() {
        //add custom calls registers
    }

    public registerCall(call: string, func: ((a: FragmentAction<keyof KnownActionData>[]) => void)) {
        this.calls.set(call, func);
    }

    public onUiEvent(e: CustomEvent<KnownActionData['uievent']>) {
        this.dispatchSimpleEvent(e.detail.e);
    }

    private async callEvent(event: string, eventParam: string) {
        this.showLoading();
        let data = await DceAjax.getInstance().get(
            this.generateEventUrl(event, eventParam)
        );

        setTimeout(() => {
            this.hideLoading();
            this.processResponse(data);
        });
    }

    public async onServerEvent(e: CustomEvent<KnownActionData["event"]>) {
        //nie mam taga, to wywołujemy na parencie aż znajdziemy
        if (!this.hasTag(e.detail.tag)) {
            if (this.parent) {
                this.parent.onServerEvent(e);
            }
            return;
        }

        this.callEvent(e.detail.e, e.detail.param);
    }

    public async onOpenModalEvent(e: CustomEvent<KnownActionData["openModal"]>) {
        let data = await this.openModal(e);
        this.processActions(data);
    }

    public async openModal(
        e: CustomEvent<KnownActionData["openModal"]>
    ): Promise<FragmentAction<keyof KnownActionData>[]> {

        return new Promise(((resolve, reject) => {

            let data = e.detail;

            let div = document.createElement("div");
            div.innerHTML = data.html;

            div.addEventListener('dceEvent', (e: CustomEvent<DceEngineEventDetails>) => {

                if (e.detail.type == DceEngineEvents.SUBCOMP_INIT_EVENT && e.detail.component == 'ModalFragment') {

                    let modalElement = e.detail.element;
                    let modalFragment = modalElement.ext.getComponent('ModalFragment') as ModalFragment;

                    modalFragment.onPassToOpener(e => {

                        console.log("PASS TO OPENER", e);

                        this.processActions(e);
                    });

                    modalFragment.onClose(actions => {
                        resolve(actions);
                    });

                }

            });

            DceEngine.getInstance().watchElement(div);

            let target = document.querySelector(data.target);
            if (target) {
                target.append(div);
            } else {
                document.body.append(div);
            }

        }));

    }


    public onReplaceFragmentEvent(e: CustomEvent<KnownActionData["rf"]>) {
        let div = document.createElement('div');
        div.innerHTML = e.detail.html;
        this.elem.replaceWith(div.firstElementChild);
    }

    public onReplaceFragmentViewEvent(e: CustomEvent<KnownActionData["rfv"]>) {
        let div = document.createElement('div');
        div.innerHTML = e.detail.html;

        this.elem.innerHTML = div.firstElementChild.innerHTML;

        setTimeout(() => {
            this.findLoadingElement();
        }, 10);

    }

    public onDom(e: CustomEvent<KnownActionData["dom"]>) {

        if (e.detail.tag && !this.hasTag(e.detail.tag)) {
            if (this.parent) {
                this.parent.onDom(e);
            }
            return;
        }

        let selector = e.detail.selector;
        let dm = e.detail.commands;

        for (const dmElement of dm) {
            DomCommands.execute(this.elem, selector, dmElement.command, dmElement.data);
        }

    }


    public async callWireAction(wireAction: string, preventLoading: boolean = false): Promise<DceAjaxResponse> {

        let parsed = wireActionParse('click', wireAction);

        if (!preventLoading) {
            this.showLoading(parsed.loadingRole);
        }

        return DceAjax.getInstance().get(
            this.generateCallUrl(parsed.action)
        ).then(res => {
            if (!preventLoading) {
                this.hideLoading(parsed.loadingRole);
            }
            return res;
        });

    }


    private generateCallUrl(action: string): string {

        let url: string[] = [FragmentsContext.route(), "wire", "action", this.props.name, this.props.props];
        let path = "/" + url.map(v => encodeURIComponent(v)).join("/");

        return path + '/' + action;
    }

    private generateEventUrl(event: string, param: string): string {
        let url: string[] = [FragmentsContext.route(), "wire", "event", this.props.name, this.props.props, event];

        if (null != param) {
            url.push(param);
        } else {
            url.push("_");
        }

        return "/" + url.map(v => encodeURIComponent(v)).join("/");
    }


    public async executeWireEvent(type: string, action: ParsedWireAction, elem: HTMLElement) {

        if (this.wireEventsLocks.has(type) && this.wireEventsLocks.get(type)) {
            return;
        }

        this.wireEventsLocks.set(type, true);

        let formData = new FormData();

        if (action.serializeSelector) {
            let form = elem.closest(action.serializeSelector);
            formData = serializeFormInputs(form as HTMLElement)
        } else {
            if (type == 'submit') {
                //formData = new FormData(elem as HTMLFormElement);
                formData = this.serializeForm(elem as HTMLFormElement);
            }
        }

        // if (action.formData) {
        //     action.formData.forEach(
        //         (value, key) => {
        //             formData.append(key, value);
        //         }
        //     );
        // }

        let url = this.generateCallUrl(action.action);

        if (action.method == 'get') {

            let gets: string[] = [];
            formData.forEach((value, key) => {
                gets.push(
                    key + '=' + encodeURIComponent(value.toString())
                );
            })

            if (gets.length > 0) {

                if (url.indexOf('?') > 0) {
                    url += '?';
                } else {
                    url += '&';
                }

                url += gets.join("&");
            }

            this.showLoading(action.loadingRole);

            let data = await DceAjax.getInstance().get(url);

            setTimeout(() => {
                this.hideLoading(action.loadingRole);
                this.processResponse(data);
            });

        }

        if (action.method == 'post') {
            this.showLoading(action.loadingRole);

            if (type == 'submit') {

                setTimeout(() => {
                    elem.dispatchEvent(new CustomEvent<any>('wiresubmit', {
                            bubbles: false,
                            detail: {
                                type: 'submit'
                            }
                        }),
                    );
                }, 0);

            }

            console.log("FORM DATA", formData);

            let data = await DceAjax.getInstance().post(
                url, formData
            );

            this.hideLoading(action.loadingRole);

            setTimeout(() => {
                this.processResponse(data);
            });
        }

        this.wireEventsLocks.set(type, false);
    }

    private processResponse(resp: DceAjaxResponse) {

        let actionresp = resp.rawData() as AjaxResp<keyof KnownActionData>;

        if (actionresp.fragmentActions) {
            this.processActions(actionresp.fragmentActions);
        }

        if (actionresp.fragmentCalls) {
            for (const calldata of actionresp.fragmentCalls) {

                let call = calldata.method;
                let data = calldata.actions;

                if (this.calls.has(call)) {
                    (this.calls.get(call))(data);
                }
            }
        }
    }

    protected processActions(actions: FragmentAction<keyof KnownActionData>[]) {
        for (const action of actions) {
            Events.dispatch(this.elem, action.type, action.data);
        }
    }

    public setParentFragment(parent: Fragment) {
        this.parent = parent;
    }

    public onSubComponentInit(component: string, elem: DceHTMLElement) {
        super.onSubComponentInit(component, elem);

        if (elem.ext.hasComponent("Fragment")) {

            this.loadingElement = this.loadingElement.filter(v => {
                return !elem.contains(v[0]);
            });

            (elem.ext.getComponent("Fragment") as Fragment).setParentFragment(this);
        }

        if (elem.ext.hasComponent("ModalFragment")) {

            this.loadingElement = this.loadingElement.filter(v => {
                return !elem.contains(v[0]);
            });

            (elem.ext.getComponent("ModalFragment") as Fragment).setParentFragment(this);
        }


    }

    onRemove() {

    }

    private findLoadingElement() {
        let foundElements = this.elem.querySelectorAll('*[data-wire-loading]');

        if (foundElements.length > 0) {
            this.loadingElement = new Array<[HTMLElement, string]>();
            foundElements.forEach(e => {
                let display = e.getAttribute('data-wire-loading');
                this.loadingElement.push(
                    [e as HTMLElement, display]
                );
            });
        }
    }

    private showLoading(role: string = '') {

        if (this.loadingElement.length == 0) {
            this.loadingCounter++;
            this.parent.showLoading(role);
            return;
        }

        if (role === '' || this.hasTag(role)) {
            this.loadingCounter++;

            this.loadingElement.forEach((d, index) => {
                d[0].style.display = d[1];
            });

            return;

        }

        if (null != this.parent) {
            this.parent.showLoading(role);
        }

    }

    private hideLoading(role: string = '') {

        if (this.loadingElement.length == 0) {
            this.loadingCounter--;
            this.parent.hideLoading(role);
            return;
        }

        if (role === '' || this.hasTag(role)) {

            this.loadingCounter--;

            if (this.loadingCounter == 0) {
                this.loadingElement.forEach((d, index) => {
                    d[0].style.display = 'none';
                });
            }

            return;
        }

        if (null != this.parent) {
            this.parent.hideLoading(role);
        }

    }

    //region ui update methods

    public update(selector: string, value: Partial<any>) {

        if (!selector) {
            return;
        }

        this.elem.querySelectorAll(selector).forEach((v) => {
            Object.assign(v, value);
        });

    }

    public updateDisplay(selector: string, value: string) {

        if (!selector) {
            return;
        }

        this.elem.querySelectorAll(selector).forEach((v) => {
            (v as HTMLElement).style.display = value;
        });

    }

    //endregion

}
